import React, { useState } from "react";
import {
  Image,
  View,
  StyleSheet,
  Pressable,
  Modal,
  Platform,
} from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import {
  MessageSquare,
  Clock,
  FileText,
  Sparkles,
  Check,
  Plus,
} from "lucide-react-native";
import { Text } from "@src/packages/medical-history/components/MedicalHistoryText";
import {
  QuestionLayout,
  IconInput,
} from "@src/packages/medical-history/components";
import { DocumentRow, MoonDateField } from "@louise/moon-ui";
import { EMBER, GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import { useDietSupplements } from "../DietSupplementsContext";
import type { SupplementDetail } from "../DietSupplementsContext";
import type { DietSupplementsStackParamList } from "../DietSupplementsNavigator";
import {
  getSupplementLabelKey,
  SUPPLEMENT_OPTIONS,
  FREQUENCY_OPTIONS,
} from "../options";
import type { FrequencyType } from "../options";
import { useMedicalHistoryTheme } from "../../../theme/MedicalHistoryTheme";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
const supplementIllustration =
  require("@src/apps/moon/assets/images/pill-capsule.png") as number;

function FrequencyPicker({
  value,
  onChange,
}: {
  value: FrequencyType | null | undefined;
  onChange: (freq: FrequencyType) => void;
}) {
  const t = useTranslations();
  const [isOpen, setIsOpen] = useState(false);
  const theme = useMedicalHistoryTheme();

  const selectedOption = value
    ? FREQUENCY_OPTIONS.find((o) => o.id === value)
    : null;
  const selectedLabel = selectedOption ? t(selectedOption.labelKey) : null;
  const capturePress = usePressCapture("SupplementFrequencySelect");

  return (
    <>
      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(
              selectedLabel ??
                t(
                  "med_history.lifestyle.diet_supplements.frequency_placeholder",
                ),
            ),
            "open_supplement_frequency",
          );
          setIsOpen(true);
        }}
        style={[
          styles.frequencyInput,
          { backgroundColor: theme.inputBg, borderColor: theme.inputBorder },
        ]}
      >
        <View style={styles.frequencyIconContainer}>
          <Clock size={16} color={theme.textFaint} />
        </View>
        <Text
          style={{
            flex: 1,
            fontSize: 14,
            marginLeft: 28,
            color: selectedLabel ? theme.text : theme.textFaint,
          }}
        >
          {selectedLabel ??
            t("med_history.lifestyle.diet_supplements.frequency_placeholder")}
        </Text>
      </Pressable>

      <Modal
        visible={isOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setIsOpen(false)}
      >
        <Pressable
          onPress={() => setIsOpen(false)}
          className="flex-1 items-center justify-center p-4"
          style={{ backgroundColor: "rgba(2, 6, 23, 0.6)" }}
        >
          {Platform.OS === "web" && (
            <View
              style={{
                position: "absolute",
                inset: 0,
                backdropFilter: "blur(4px)",
                WebkitBackdropFilter: "blur(4px)",
              }}
            />
          )}
          <Pressable
            onPress={(e) => e.stopPropagation()}
            className="w-full max-w-[340px] overflow-hidden rounded-2xl border border-white/15"
            style={{
              backgroundColor: "#1E293B",
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 12 },
              shadowOpacity: 0.5,
              shadowRadius: 32,
              elevation: 24,
            }}
          >
            <View className="border-b border-white/5 px-4 py-3">
              <Text
                style={{ fontSize: 14, fontWeight: "600", color: theme.text }}
              >
                {t(
                  "med_history.lifestyle.diet_supplements.frequency_modal_title",
                )}
              </Text>
            </View>
            <View className="p-2">
              {FREQUENCY_OPTIONS.map((option) => {
                const isSelected = value === option.id;
                return (
                  <Pressable
                    key={option.id}
                    onPress={() => {
                      capturePress(
                        pressLabel(t(option.labelKey)),
                        "supplement_frequency",
                      );
                      onChange(option.id);
                      setIsOpen(false);
                    }}
                    className={`flex-row items-center justify-between rounded-xl px-4 py-3 ${
                      isSelected ? "bg-[#C4B5FD]/10" : "hover:bg-white/5"
                    }`}
                  >
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: isSelected ? "600" : "400",
                        color: isSelected ? theme.accentLight : theme.textMuted,
                      }}
                    >
                      {t(option.labelKey)}
                    </Text>
                    {isSelected && (
                      <Check size={16} color={theme.accentLight} />
                    )}
                  </Pressable>
                );
              })}
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

export function SupplementsDetailsScreen() {
  const t = useTranslations();
  const { state, updateState } = useDietSupplements();
  const navigation =
    useNavigation<NativeStackNavigationProp<DietSupplementsStackParamList>>();
  const theme = useMedicalHistoryTheme();

  const updateSupplement = (
    index: number,
    updates: Partial<SupplementDetail>,
  ) => {
    const updated = state.supplements.map((s, i) =>
      i === index ? { ...s, ...updates } : s,
    );
    updateState({ supplements: updated });
  };

  const addSupplement = (supplement: SupplementDetail["supplement"]) => {
    updateState({
      supplements: [...state.supplements, { supplement }],
    });
  };

  const handleSave = () => {
    navigation.navigate("DietSupplementsComplete");
  };

  return (
    <QuestionLayout
      title={t("med_history.lifestyle.diet_supplements.details_title")}
      subtitle={t("med_history.lifestyle.diet_supplements.details_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={false}
      saveLabel={t("med_history.lifestyle.next")}
    >
      <View style={styles.container}>
        {state.supplements.map((entry, index) => {
          const isHerbal = entry.supplement === "herbal";
          const isOther = entry.supplement === "other";
          const label = t(getSupplementLabelKey(entry.supplement));
          const SupplementIcon =
            SUPPLEMENT_OPTIONS.find((o) => o.id === entry.supplement)?.icon ??
            Sparkles;

          return (
            <View key={index} style={[GLASS_CARD_STYLE, styles.detailCard]}>
              <View style={styles.cardHeader}>
                <View style={styles.cardTitleRow}>
                  <SupplementIcon size={14} color={theme.accentLight} />
                  <Text style={[styles.cardTitle, { color: theme.text }]}>
                    {label}
                  </Text>
                </View>
                <Text style={[styles.cardSubtitle, { color: theme.textFaint }]}>
                  {t(
                    "med_history.lifestyle.diet_supplements.details_card_subtitle",
                  )}
                </Text>
              </View>

              {(isHerbal || isOther) && (
                <IconInput
                  icon={FileText}
                  label={t(
                    isHerbal
                      ? "med_history.lifestyle.diet_supplements.herbal_label"
                      : "med_history.lifestyle.diet_supplements.other_supplement_label",
                  )}
                  placeholder={
                    isHerbal
                      ? t(
                          "med_history.lifestyle.diet_supplements.herbal_placeholder",
                        )
                      : t(
                          "med_history.lifestyle.diet_supplements.other_supplement_placeholder",
                        )
                  }
                  value={entry.other ?? ""}
                  onChangeText={(text) =>
                    updateSupplement(index, { other: text || null })
                  }
                />
              )}

              <MoonDateField
                label={t(
                  "med_history.lifestyle.diet_supplements.start_date_label",
                )}
                placeholder={t(
                  "med_history.lifestyle.diet_supplements.start_date_placeholder",
                )}
                value={entry.startDate ?? ""}
                onChange={(iso) =>
                  updateSupplement(index, { startDate: iso || null })
                }
              />

              <FrequencyPicker
                value={entry.frequency}
                onChange={(freq) =>
                  updateSupplement(index, { frequency: freq })
                }
              />

              <IconInput
                icon={MessageSquare}
                label={t("med_history.lifestyle.diet_supplements.notes_label")}
                placeholder={t(
                  "med_history.lifestyle.diet_supplements.notes_placeholder",
                )}
                value={entry.notes ?? ""}
                onChangeText={(text) =>
                  updateSupplement(index, { notes: text || null })
                }
                multiline
                numberOfLines={3}
              />
            </View>
          );
        })}

        <DocumentRow
          onPress={() => {
            // Add another of the first supplement type as default
            const firstType = state.supplements[0]?.supplement ?? "folic_acid";
            addSupplement(firstType);
          }}
          accessibilityLabel={t(
            "med_history.lifestyle.diet_supplements.add_another",
          )}
          leading={
            <Image
              source={supplementIllustration}
              resizeMode="contain"
              style={styles.addIllustration}
            />
          }
          title={t("med_history.lifestyle.diet_supplements.add_another")}
          trailing={
            <Plus size={18} color={EMBER.colors.textLight} strokeWidth={1.5} />
          }
          showChevron={false}
          style={styles.addRow}
        />
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
    gap: 16,
  },
  detailCard: {
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    gap: 16,
  },
  cardHeader: {
    gap: 4,
  },
  cardTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: "700",
  },
  cardSubtitle: {
    fontSize: 12,
  },
  frequencyInput: {
    position: "relative",
    width: "100%",
    borderWidth: 1,
    borderRadius: 12,
    paddingVertical: 12,
    paddingLeft: 12,
    paddingRight: 16,
    flexDirection: "row",
    alignItems: "center",
  },
  frequencyIconContainer: {
    position: "absolute",
    left: 12,
    top: 14,
    zIndex: 1,
    pointerEvents: "none",
  },
  addRow: {
    width: "100%",
  },
  addIllustration: {
    width: 34,
    height: 34,
  },
});
